import { Add } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridColumns } from '@mui/x-data-grid';
import { EditableCard } from 'src/careflow/common/EditableCard';
import {
  EpisodeOfCareStatusHistory,
  ValueSetComposeIncludeConcept,
} from 'src/careflow/ph-core/fhir-client/resources';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import LoadableAutocomplete from 'src/components/LoadableAutocomplete';
import useObjectState from 'src/hooks/useObjectState';
import { shortFormat } from 'src/utils/formatTime';
import uuidv4 from 'src/utils/uuidv4';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import CancelButton from 'src/careflow/common/CancelButton';
import { MobileDatePicker } from '@mui/lab';
import moment from 'moment';
import Iconify from 'src/components/Iconify';
import { useMemo } from 'react';
import * as Yup from 'yup';
import { mapYupErrors } from 'src/careflow/ph-core/fhir-organization/components/common/util';
import { isTimeIncludedInDate } from 'src/utils/dates';
import { WrappedEpisodeOfCare } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/EpisodeOfCare';

type ComparableStatusHistory = Partial<EpisodeOfCareStatusHistory>;

export const isStatusHistoryDuplicate = (
  statusHistory: EpisodeOfCareStatusHistory[],
  candidate: ComparableStatusHistory,
  ignoredIndex: number | null = null
) => {
  const datesMatch = (first?: string, second?: string) => {
    if (!first || !second) return first === second;
    return moment(first).isSame(moment(second), 'day');
  };

  return statusHistory.some(
    (item, index) =>
      index !== ignoredIndex &&
      item.status === candidate.status &&
      datesMatch(item.period?.start, candidate.period?.start) &&
      datesMatch(item.period?.end, candidate.period?.end)
  );
};

interface StatusHistoryState extends Partial<EpisodeOfCareStatusHistory> {
  isModalOpen: boolean;
  editingIndex: number | null;
  period: {
    start?: string;
    end?: string;
  };
  statusCustom: any | null;
  errors: any;
}

type Props = {
  eoc: WrappedEpisodeOfCare | null;
  i18n: i18nFunction;
  handle: Function;
};

type StatusHistoryProps = {
  statusHistory: (EpisodeOfCareStatusHistory & { displayStatus?: string })[];
  i18n: i18nFunction;
  onEdit?: (row: EpisodeOfCareStatusHistory & { index: number }) => void;
};

const StatusHistory = ({ eoc, i18n, handle }: Props) => {
  const [{ isModalOpen, editingIndex, statusCustom, period, errors }, updateState] =
    useObjectState<StatusHistoryState>({
      isModalOpen: false,
      editingIndex: null,
      statusCustom: null,
      period: {
        start: undefined,
        end: undefined,
      },
      errors: {},
    });

  const [eocStatusVS, { isLoading: isEocStatusVSLoading }] = useValueSet('episode-of-care-status', {
    map: ValueSetWrapper,
    autofetch: !!eoc,
  });

  const schema = Yup.object().shape({
    statusCustom: Yup.object()
      .nullable()
      .required(i18n('episodeOfCare.details.statusHistory.errors.statusCustom', 'crs')),
    period: Yup.object()
      .shape({
        start: Yup.date().nullable(),
        end: Yup.date()
          .nullable()
          .test(
            'is-greater',
            i18n('episodeOfCare.details.statusHistory.errors.end', 'crs'),
            function (value) {
              const { start } = this.parent;

              if (!start || !value) {
                return true;
              }

              const startDate = moment(start);
              const endDate = moment(value);

              return startDate.isSameOrBefore(
                endDate,
                isTimeIncludedInDate(endDate) && isTimeIncludedInDate(startDate)
                  ? undefined
                  : 'date'
              );
            }
          ),
      })
      .test(
        'at-least-one-date',
        i18n('episodeOfCare.details.statusHistory.errors.period', 'crs'),
        (value) => Boolean(value?.start || value?.end)
      ),
  });

  const saveChanges = async () => {
    const validateFields = await schema
      .validate({ statusCustom, period }, { abortEarly: false })
      .catch((err) => err.inner);

    const errors = validateFields?.length ? mapYupErrors(validateFields) : {};
    updateState({ errors: errors });
    if (validateFields?.length) return;

    const { statusHistory, ...rest } = eoc ?? {};

    const newStatus = {
      ...(statusCustom && { status: statusCustom.code }),
      ...((period?.start || period?.end) && {
        period: {
          ...(period?.start && { start: period.start }),
          ...(period?.end && { end: period.end }),
        },
      }),
    };

    if (isStatusHistoryDuplicate(statusHistory ?? [], newStatus, editingIndex)) {
      updateState({
        errors: {
          duplicate: i18n('episodeOfCare.details.statusHistory.errors.duplicate', 'crs'),
        },
      });
      return;
    }

    const statusHistoryUpdated = [...(statusHistory ?? [])];

    if (editingIndex !== null) {
      statusHistoryUpdated[editingIndex] = newStatus;
    } else {
      statusHistoryUpdated.push(newStatus);
    }

    const payload = {
      ...rest,
      statusHistory: statusHistoryUpdated,
    };

    handle(payload);
    onClose();
  };

  const onClose = () => {
    updateState({
      isModalOpen: false,
      editingIndex: null,
      statusCustom: null,
      period: {
        start: undefined,
        end: undefined,
      },
      errors: {},
    });
  };

  const onEdit = (row: EpisodeOfCareStatusHistory & { index: number }) => {
    updateState({
      editingIndex: row.index,
      statusCustom: eocStatusVS?.asListAll()?.find((item) => item.code === row.status) ?? null,
      ...(row?.period && {
        period: {
          ...(row.period.start && { start: row.period.start }),
          ...(row.period.end && { end: row.period.end }),
        },
      }),
      isModalOpen: true,
    });
  };

  const mappedStatusHistory = useMemo(
    () =>
      eoc?.statusHistory?.map((item, index) => ({
        ...item,
        displayStatus:
          eocStatusVS?.asListAll()?.find((e) => e?.code === item?.status)?.display ?? item?.status,
        index,
      })) ?? [],
    [eoc?.statusHistory, eocStatusVS]
  );

  return (
    <>
      <EditableCard
        title={i18n('episodeOfCare.details.general.statusHistory', 'crs')}
        showEditButton={false}
      >
        <Box sx={{ position: 'absolute', top: '20px', right: '25px' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => updateState({ isModalOpen: true })}
          >
            {i18n('episodeOfCare.details.statusHistory.buttonAdd', 'crs')}
          </Button>
        </Box>
        <StatusHistoryTable statusHistory={mappedStatusHistory} i18n={i18n} onEdit={onEdit} />
      </EditableCard>
      <Dialog fullWidth open={isModalOpen}>
        <DialogTitle sx={{ mb: 1 }}>
          {i18n('episodeOfCare.details.statusHistory.buttonAdd', 'crs')}
        </DialogTitle>
        <DialogContent>
          <LoadableAutocomplete
            fullWidth
            sx={{ mt: 1 }}
            label={`${i18n('episodeOfCare.details.statusHistory.status', 'crs')} *`}
            isLoading={isEocStatusVSLoading}
            value={statusCustom}
            onChange={(_, newValue) => {
              updateState({ errors: {}, statusCustom: newValue });
            }}
            error={errors?.statusCustom ?? ''}
            options={eocStatusVS?.asListAll?.() ?? []}
            getOptionLabel={({ display }: ValueSetComposeIncludeConcept) => display ?? ''}
          />

          <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
            <MobileDatePicker
              value={period?.start ?? null}
              label="Start"
              inputFormat="MM/dd/yyyy"
              onChange={(date) =>
                updateState({
                  errors: {},
                  period: { ...period, start: moment(date).toISOString() },
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={Boolean(errors.period)}
                  helperText={errors.period}
                />
              )}
            />
            <MobileDatePicker
              value={period?.end ?? null}
              label="End"
              inputFormat="MM/dd/yyyy"
              onChange={(date) =>
                updateState({ errors: {}, period: { ...period, end: moment(date).toISOString() } })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={Boolean(errors['period.end'] || Boolean(errors.period))}
                  helperText={errors['period.end']}
                />
              )}
            />
          </Stack>
          {errors.duplicate && (
            <Typography color="error" variant="caption">
              {errors.duplicate}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <CancelButton onClick={onClose}>{i18n('cancel')}</CancelButton>

          <Button variant="contained" type="submit" onClick={saveChanges}>
            {i18n('saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const StatusHistoryTable = ({ statusHistory, i18n, onEdit }: StatusHistoryProps) => {
  const actionColumn: GridColumns = onEdit
    ? [
        {
          field: 'actions',
          type: 'actions',
          getActions: ({ row }) => [
            <GridActionsCellItem
              key={uuidv4()}
              icon={
                <>
                  <Iconify icon={'eva:edit-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
                  {i18n('edit')}
                </>
              }
              label={''}
              onClick={() => onEdit?.(row)}
              showInMenu={true}
            />,
          ],
        },
      ]
    : [];

  const columns: GridColDef<EpisodeOfCareStatusHistory & { displayStatus?: string }>[] = [
    {
      field: 'status',
      headerName: `${i18n('episodeOfCare.details.statusHistory.status', 'crs')}`,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const { row } = params;
        return <Typography>{row?.displayStatus}</Typography>;
      },
    },
    {
      field: 'startDate',
      headerName: `${i18n('episodeOfCare.details.statusHistory.startDate', 'crs')}`,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const { row } = params;
        return (
          <Typography>
            {row?.period?.start ? shortFormat(new Date(row?.period?.start)) : ''}
          </Typography>
        );
      },
    },
    {
      field: 'endDate',
      headerName: `${i18n('episodeOfCare.details.statusHistory.endDate', 'crs')}`,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const { row } = params;
        return (
          <Typography>{row?.period?.end ? shortFormat(new Date(row?.period?.end)) : ''}</Typography>
        );
      },
    },
    // ...actionColumn, wait for rules
  ];

  return (
    <Box sx={{ width: '100%', height: 350, mt: 2 }}>
      <DataGrid
        rows={statusHistory ?? []}
        columns={columns}
        loading={false}
        rowSpacingType="margin"
        getRowId={() => uuidv4()}
        getRowSpacing={() => ({ bottom: 5 })}
        getRowHeight={() => 'auto'}
        getEstimatedRowHeight={() => 90}
        isRowSelectable={() => false}
        disableColumnFilter
        disableColumnMenu
        density={'compact'}
        components={{
          Footer: () => <></>,
        }}
        className="dataGridWithFilter"
        sx={[
          {
            width: '100%',
            '& .MuiDataGrid-row': {
              borderBottom: '1px solid #EAECF0',
              '&:nth-of-type(odd)': {
                backgroundColor: '#FAFAFA',
              },
              '&:hover': {
                backgroundColor: '#F5F6F7',
              },
            },
          },
        ]}
      />
    </Box>
  );
};

export default StatusHistory;
